// TraitementDiversManager.jsx — Redesign Premium matching other modules
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, AlertCircle,
    FileText, ChevronDown, Download, MessageSquare, Loader, Eye, Clock, RotateCcw,
} from "lucide-react";

import {
    getManagerAttestationInboxRequest,
    managerAttestationDecisionRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import { exportTreatmentExcel } from "./exportTreatmentExcel";

import "../TraitementStyles/TraitementNotesFrais.css";
import "../TraitementStyles/TreatmentMesOrdres.css";

/* ── SearchableCombobox ── */
const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => { setQuery(opt.label); onChange(opt.value); setOpen(false); };
    const handleInputChange = (e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); };
    const handleClear = () => { setQuery(""); onChange(""); setOpen(false); };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Effacer">
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title="Afficher la liste">
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    Aucun résultat
                </div>
            )}
        </div>
    );
};

const TraitementDiversManager = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const attState = useSelector((state) => state.attestations || {});
    const managerInbox = attState.managerInbox || [];
    const loading = !!attState.loading;
    const apiError = attState.error;
    const apiMessage = attState.message;

    /* ── Local state ─────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const delegation = useTreatmentDelegation({
        endpoint: "/api/attestations/manager/treatment-delegation",
        label: "d'attestations",
        onUpdated: () => dispatch(getManagerAttestationInboxRequest()),
    });

    useEffect(() => {
        dispatch(getManagerAttestationInboxRequest());
    }, [dispatch]);

    useEffect(() => {
        if (apiError || apiMessage) {
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, apiMessage, dispatch]);

    /* ── Helpers ─────────────────────────────────────────────── */
    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return d;
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const mapDemandeToTab = (demande) => {
        if (demande.managerDecision === true) return "approuve";
        if (demande.managerDecision === false) return "rejete";
        if (String(demande.status || demande.statut || "").toUpperCase() === "PENDING_MANAGER") return "enCours";
        return null;
    };

    const demandes = useMemo(() => {
        return (managerInbox || []).map((d) => {
            const att = d.attestation || {};
            const statusRaw = String(d.status || d.statut).toUpperCase();
            const statut = mapDemandeToTab(d);
            return {
                id: d.id,
                matricule: d.matricule || d.code || "—",
                collaborateur: d.fullName || d.nomComplet || `${d.employeeNom || ""} ${d.employeePrenom || ""}`.trim() || "—",
                poste: d.poste || d.jobTitle || d.posteNom || "—",
                departement: d.departement || d.department || d.departementNom || "—",
                typeAttestation: d.attestationTitre || att.titre || att.nom || "Attestation",
                couleurType: d.couleurType || att.couleur || "#3b82f6",
                dateDemande: d.createdAt || d.dateDemande || "",
                motif: d.motif || d.commentaire || "",
                attachments: d.attachments || d.documentsJoints || [],
                managerComment: d.managerComment || "",
                managerDecision: d.managerDecision ?? null,
                adminDecision: d.adminDecision ?? null,
                statut,
                statusRaw,
                canDownload: statusRaw === "APPROVED",
            };
        });
    }, [managerInbox]);

    const getStatutBadge = (statut, statusRaw) => {
        const config = {
            enCours: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: {
                label: statusRaw === "PENDING_ADMIN" ? "Approuvé (RH en cours)" : "Approuvé",
                bg: "#d4edda",
                color: "#155724"
            },
            rejete: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tdBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const filtered = useMemo(() => {
        let list = demandes.filter((d) => d.statut === activeTab);

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            list = list.filter(
                (d) =>
                    d.collaborateur.toLowerCase().includes(val) ||
                    d.matricule.toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            list = list.filter((d) => (d.typeAttestation || "").toLowerCase().includes(val));
        }
        if (filterDate) {
            list = list.filter((d) => {
                if (!d.dateDemande) return false;
                return String(d.dateDemande).slice(0, 10) === filterDate;
            });
        }
        return list;
    }, [demandes, activeTab, filterCollab, filterType, filterDate]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: demandes.filter(d => d.statut === "enCours").length },
        { id: "approuve", label: "Traitées", count: demandes.filter(d => d.statut === "approuve" || d.statut === "rejete").length },
    ], [demandes]);

    /* Adapt tabs counts to match the two-tab approach if that's what manager view uses */
    const managerTabs = useMemo(() => {
        const enCoursCount = demandes.filter(d => d.statut === "enCours").length;
        const traiteCount = demandes.filter(d => d.statut === "approuve" || d.statut === "rejete").length;
        return [
            { id: "enCours", label: "En attente", count: enCoursCount },
            { id: "traite", label: "Traitées", count: traiteCount },
        ];
    }, [demandes]);

    const workflowTabs = useMemo(() => [
        { id: "enCours", label: "En attente", count: demandes.filter(d => d.statut === "enCours").length },
        { id: "approuve", label: "Approuvees", count: demandes.filter(d => d.statut === "approuve").length },
        { id: "rejete", label: "Rejetees", count: demandes.filter(d => d.statut === "rejete").length },
    ], [demandes]);

    // Use traite logic if activeTab is "traite"
    const finalFiltered = useMemo(() => {
        return demandes.filter(d => d.statut === activeTab);
    }, [demandes, activeTab]);

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        demandes.forEach((d) => {
            if (d.collaborateur && !map.has(d.collaborateur)) {
                map.set(d.collaborateur, { value: d.collaborateur, label: d.matricule ? `${d.collaborateur} (${d.matricule})` : d.collaborateur });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [demandes]);

    const typeOptions = useMemo(() => {
        return [...new Set(demandes.map((d) => d.typeAttestation).filter(Boolean))]
            .sort()
            .map((t) => ({ value: t, label: t }));
    }, [demandes]);

    /* Actually, let's stick to the filtered logic with the tab toggle */
    const listToDisplay = useMemo(() => {
        let list = finalFiltered;
        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            list = list.filter(
                (d) =>
                    d.collaborateur.toLowerCase().includes(val) ||
                    d.matricule.toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            list = list.filter((d) => (d.typeAttestation || "").toLowerCase().includes(val));
        }
        if (filterDate) {
            list = list.filter((d) => {
                if (!d.dateDemande) return false;
                return String(d.dateDemande).slice(0, 10) === filterDate;
            });
        }
        return list;
    }, [finalFiltered, filterCollab, filterType, filterDate]);

    /* ── Actions ─────────────────────────────────────────────── */
    const handleApprove = (id) => dispatch(managerAttestationDecisionRequest({ id, approved: true, comment: "" }));
    const handleRejectClick = (d) => { setSelectedDemande(d); setMotifRejet(""); setShowRejectModal(true); };
    const confirmReject = () => {
        if (!selectedDemande || !motifRejet.trim()) return;
        dispatch(managerAttestationDecisionRequest({ id: selectedDemande.id, approved: false, comment: motifRejet }));
        setShowRejectModal(false);
    };

    const handleDownload = (d) => dispatch(downloadAttestationRequestPdfRequest({ requestId: d.id, filename: `${d.typeAttestation}.pdf` }));

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && demandes.length === 0) {
        return (
            <div className="tdPage">
                <div className="tdWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>Traitement des demandes d'attestations</h1>
                        <p>Validez les demandes d'attestations des membres de votre équipe</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">Délégation Admin RH</div>
                            <div className="tavDelegationSetting__sub">Transmettre mon traitement des attestations à l'admin RH</div>
                        </div>
                        <label className="tavSwitch" title="Délégation Admin RH">
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Collaborateur / Matricule
                            </label>
                            <SearchableCombobox
                                value={filterCollab}
                                onChange={setFilterCollab}
                                options={collaborateurOptions}
                                placeholder="Nom ou matricule..."
                            />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Type d'attestation
                            </label>
                            <SearchableCombobox
                                value={filterType}
                                onChange={setFilterType}
                                options={typeOptions}
                                placeholder="Rechercher un type..."
                            />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>
                                Date de demande
                            </label>
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                className="dcInput"
                                style={{ width: "100%", fontSize: "13px" }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => exportTreatmentExcel(filtered, "Attestations_manager")}><Download size={14} /> Exporter Excel</button>
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="dcBtn dcBtn--outline"
                                style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}
                            >
                                <RotateCcw size={14} /> Réinitialiser
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {workflowTabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tnfTab ${activeTab === tab.id ? "is-active" : (activeTab === "traite" && tab.id === "traite" ? "is-active" : "")}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {listToDisplay.length === 0 ? (
                            <div className="tnfEmpty"><AlertCircle size={46} /><div>Aucune demande trouvée</div></div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Département</th>
                                        <th>Type d'attestation</th>
                                        <th>Date de la demande</th>
                                        <th>Statut</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listToDisplay.map(d => (
                                        <tr key={d.id} onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }} style={{ cursor: "pointer" }}>
                                            <td><span className="tnfPrimary">{d.matricule}</span></td>
                                            <td>
                                                <div className="tnfPrimary">{d.collaborateur}</div>
                                                <div className="tnfSub">{d.poste}</div>
                                            </td>
                                            <td><span className="tnfMuted">{d.departement}</span></td>
                                            <td>
                                                <div className="tnfType">
                                                    <span className="tnfDot" style={{ backgroundColor: d.couleurType }} />
                                                    <span className="tnfPrimary">{d.typeAttestation}</span>
                                                </div>
                                            </td>
                                            <td><div className="tnfDate"><Clock size={14} />{formatDate(d.dateDemande)}</div></td>
                                            <td>{getStatutBadge(d.statut, d.statusRaw)}</td>
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                <div className="tdRowActions">
                                                    {activeTab === "enCours" ? (
                                                        <>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--ok" onClick={() => handleApprove(d.id)} title="Approuver"><Check size={16} /></button>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--no" onClick={() => handleRejectClick(d)} title="Refuser"><X size={16} /></button>
                                                            <button type="button" className="tnfActionBtn" onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }} title="Détails" style={{ background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe" }}><Eye size={16} /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="tnfDetailsBtn" onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }}>Détails</button>
                                                            {d.canDownload && <button type="button" className="tnfActionBtn tnfActionBtn--dl" onClick={() => handleDownload(d)} title="Télécharger"><Download size={16} /></button>}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon"><FileText size={18} /></div>
                                <div><div className="tdModal__title">Détails de la demande</div><div className="tdModal__sub">{selectedDemande.typeAttestation}</div></div>
                            </div>
                            <button type="button" className="tdIconBtn" onClick={() => setShowDetailsModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tdModal__body">
                            <div className="tdGrid2">
                                <div className="tdPanel">
                                    <div className="tdPanel__title">Collaborateur</div>
                                    <div className="tdKv"><span>Matricule</span><b>{selectedDemande.matricule}</b></div>
                                    <div className="tdKv"><span>Nom</span><b>{selectedDemande.collaborateur}</b></div>
                                    <div className="tdKv"><span>Poste</span><b>{selectedDemande.poste}</b></div>
                                </div>
                                <div className="tdPanel">
                                    <div className="tdPanel__title">Demande</div>
                                    <div className="tdKv"><span>Type</span><b>{selectedDemande.typeAttestation}</b></div>
                                    <div className="tdKv"><span>Soumise le</span><b>{formatDate(selectedDemande.dateDemande)}</b></div>
                                    <div className="tdKv"><span>Statut</span><b>{getStatutBadge(selectedDemande.statut, selectedDemande.statusRaw)}</b></div>
                                </div>
                                <div className="tdPanel tdPanel--full">
                                    <div className="tdPanel__title"><MessageSquare size={16} /> Motif de la demande</div>
                                    <div className="tdText">{selectedDemande.motif || "Aucun motif précisé"}</div>
                                </div>
                                {selectedDemande.managerComment && (
                                    <div className={`tdPanel tdPanel--full ${selectedDemande.statut === "approuve" ? "tdValidOk" : "tdValidNo"}`}>
                                        <div className="tdPanel__title">Décision Manager</div>
                                        <div className="tdRejectBox">
                                            <div className="tdRejectLabel">Commentaire</div>
                                            <div className="tdRejectText">{selectedDemande.managerComment}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="tdModal__footer">
                            <button className="tdBtn tdBtn--ghost" onClick={() => setShowDetailsModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedDemande && (
                <div className="tdModal">
                    <div className="tdModal__panel tdModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon tdModal__icon--danger"><AlertCircle size={18} /></div>
                                <div><div className="tdModal__title">Refuser la demande</div><div className="tdModal__sub">{selectedDemande.collaborateur}</div></div>
                            </div>
                            <button type="button" className="tdIconBtn" onClick={() => setShowRejectModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tdModal__body">
                            <label className="tdLabel">Motif du rejet <span className="tdReq">*</span></label>
                            <textarea className="tdTextarea" rows={4} value={motifRejet} onChange={e => setMotifRejet(e.target.value)} placeholder="Précisez la raison du refus..." />
                        </div>
                        <div className="tdModal__footer">
                            <button className="tdBtn tdBtn--ghost" onClick={() => setShowRejectModal(false)}>Annuler</button>
                            <button className="tdBtn tdBtn--danger" disabled={!motifRejet.trim()} onClick={confirmReject}>Confirmer le rejet</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementDiversManager;
